import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BackButton from '../../components/Common/BackButton';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import { signInStudentWithConfirmation } from '../../store/thunks/user.thunk';
import Loader from '../../components/Common/Loader';
import { AppDispatch } from '../../store/store';
import { useDispatch } from 'react-redux';

export interface ParentPermissionForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isGuardian: boolean;
  confirmShare?: boolean;
  marketing: boolean;
}

const ParentPermission: React.FC = () => {
  const navigateTo = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isGuardian: false,
      confirmShare: false,
      marketing: false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      phone: Yup.string().optional(),
      isGuardian: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
      confirmShare: Yup.boolean(),
      marketing: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(signInStudentWithConfirmation({ formData: values, navigateTo }));
      } catch (error) {
        console.log(error);
      } finally {
      }
    },
  });

  return (
    <div className="min-h-screen bg-whiteGrayBackground flex flex-col justify-center items-center font-sofiaSans">
      <form
        className="w-full max-w-sm px-8 flex flex-col items-center"
        onSubmit={formik.handleSubmit}
        autoComplete="off"
      >
        <div className="text-left mb-8 mt-2 w-full">
          <h1
            className="text-3xl sm:text-4xl font-extrabold text-black leading-tight mb-4"
            style={{ letterSpacing: '-0.02em' }}
          >
            Help Your Child
            <br />
            Start Their Story
          </h1>
          <p className="text-base text-black leading-relaxed max-w-xs">
            Your child was invited to create their own story. Just give permission below so they can get started.
          </p>
        </div>
        <div className="flex w-full gap-4 mb-5">
          <div className="relative flex-1">
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              className={`flex-1 px-3 py-2 w-full border ${
                formik.touched.firstName && formik.errors.firstName
                  ? 'border-storyseconderror'
                  : 'border-secondaryStoryGrayBorder'
              } rounded-lg text-base text-secondaryStoryBlackText focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText`}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <div className="absolute text-storyseconderror text-sm">{formik.errors.firstName}</div>
            )}
          </div>
          <div className="relative flex-1">
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              className={`flex-1 px-3 py-2 w-full border ${
                formik.touched.lastName && formik.errors.lastName
                  ? 'border-storyseconderror'
                  : 'border-secondaryStoryGrayBorder'
              } rounded-lg text-base text-secondaryStoryBlackText focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText`}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <div className="absolute text-storyseconderror text-sm mt-0.5">{formik.errors.lastName}</div>
            )}
          </div>
        </div>
        <div className="relative w-full mb-5">
          <input
            type="email"
            name="email"
            placeholder="Email"
            className={`w-full px-3 py-2 border ${
              formik.touched.email && formik.errors.email
                ? 'border-storyseconderror'
                : 'border-secondaryStoryGrayBorder'
            } rounded-lg text-base text-secondaryStoryBlackText focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText`}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.email && formik.errors.email && (
            <div className="absolute text-storyseconderror text-sm mt-0.5">{formik.errors.email}</div>
          )}
        </div>
        <div className="relative w-full mb-5">
          <PhoneInput
            value={formik.values.phone}
            onChange={(phone) => formik.setFieldValue('phone', phone)}
            onBlur={() => formik.setFieldTouched('phone', true)}
            placeholder="Mobile Number (Optional)"
            inputClass={`!w-full !px-4 !pl-12 !py-2 !border ${
              formik.touched.phone && formik.errors.phone
                ? '!border-storyseconderror'
                : '!border-secondaryStoryGrayBorder'
            } !rounded-lg !text-base !text-secondaryStoryBlackText`}
            containerClass="!w-full"
            buttonClass="!border !border-secondaryStoryGrayBorder !rounded-l-lg !bg-white hover:!bg-gray-50"
            dropdownClass="!border !border-secondaryStoryGrayBorder !rounded-lg !shadow-lg"
            preferredCountries={['us', 'ca', 'gb', 'au']}
            inputStyle={{
              width: '100%',
              height: '42px',
              fontSize: '16px',
              paddingLeft: '48px',
              border: '1px solid',
              borderRadius: '8px',
              outline: 'none',
            }}
            containerStyle={{
              width: '100%',
            }}
            buttonStyle={{
              border: '1px solid',
              borderRadius: '8px 0 0 8px',
              backgroundColor: 'white',
            }}
          />
          {formik.touched.phone && formik.errors.phone && (
            <div className="absolute text-storyseconderror text-sm mt-0.5">{formik.errors.phone}</div>
          )}
        </div>
        <div className="w-full flex items-start mb-2 mt-2 text-left">
          <input
            id="isGuardian"
            name="isGuardian"
            type="checkbox"
            checked={formik.values.isGuardian}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`mt-1 h-4 w-4 text-secondaryStoryAdditionalLightBlueText border-gray-300 rounded focus:ring-secondaryStoryAdditionalLightBlueText`}
          />

          <label
            htmlFor="isGuardian"
            className={`ml-2 text-xs ${formik.touched.isGuardian && formik.errors.isGuardian ? 'text-storyseconderror' : 'text-secondaryStoryBlackText'} select-none`}
          >
            <span className="font-bold mr-1">Required:</span>I confirm I am the child’s parent or legal guardian and I
            give permission for them to use Build My Story. I agree to the{' '}
            <Link
              to="https://buildmystory.com/terms-and-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondaryStoryAdditionalLightBlueText hover:underline"
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              to="https://buildmystory.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondaryStoryAdditionalLightBlueText hover:underline"
            >
              Privacy Policy
            </Link>
          </label>
        </div>
        <div className="w-full flex items-start mb-6 text-left">
          <input
            id="confirmShare"
            name="confirmShare"
            type="checkbox"
            checked={formik.values.confirmShare}
            onChange={formik.handleChange}
            className="mt-1 h-4 w-4 text-secondaryStoryOrangeText border-gray-300 rounded focus:ring-secondaryStoryOrangeText"
          />
          <label htmlFor="confirmShare" className="ml-2 text-xs text-secondaryStoryBlackText select-none">
            <span className="font-bold mr-1">Optional:</span>Yes, you can share my child’s book to help inspire other
            kids, including on your site, social, or promos.
          </label>
        </div>
        <div className="w-full flex items-start mb-6 text-left">
          <input
            id="marketing"
            name="marketing"
            type="checkbox"
            checked={formik.values.marketing}
            onChange={formik.handleChange}
            className="mt-1 h-4 w-4 text-secondaryStoryAdditionalLightBlueText border-gray-300 rounded focus:ring-secondaryStoryAdditionalLightBlueText"
          />
          <label htmlFor="marketing" className="ml-2 text-xs text-secondaryStoryBlackText select-none">
            Yes, I'd like to receive tips, free resources, and occasional updates.
          </label>
        </div>
        <button
          type="submit"
          className="w-full py-2 mb-2 flex items-center justify-center rounded-lg bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold text-base shadow-md hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={formik.isSubmitting}
        >
          Continue
          <Loader loader={formik.isSubmitting} />
        </button>
        <div className="flex items-center justify-between w-full">
          <div>
            <BackButton onClick={() => window.history.back()}></BackButton>
          </div>
          <img src="/new_black_logo.png" alt="Build My Story" className="h-6" />
        </div>
      </form>
    </div>
  );
};

export default ParentPermission;
